/**
 * Locked-tier strips.
 *
 * Copy states what the agency is missing in their own terms, not a feature list.
 * The design's headline figure ("Fourteen viewing requests this week were taken
 * as messages") is a real count, so it is passed in from live data — and when
 * that count is zero or unavailable the strip falls back to a claim-free line
 * rather than asserting a number nobody has measured.
 */
import { border, c, lockedGradient, mono, radius } from '../theme/tokens';
import { LinkButton, Mono, TierPill } from './primitives';

export function ProStrip({
  viewingsAsMessages,
  onOpen,
  ghosts = true,
}: {
  viewingsAsMessages: number | null;
  onOpen: () => void;
  ghosts?: boolean;
}) {
  const tease =
    viewingsAsMessages && viewingsAsMessages > 0
      ? `${viewingsAsMessages} viewing ${viewingsAsMessages === 1 ? 'request' : 'requests'} in this range ${viewingsAsMessages === 1 ? 'was' : 'were'} taken as a message. On Pro they go straight into the diary.`
      : 'Viewing requests are taken as messages for the team to confirm. On Pro they go straight into the diary.';

  return (
    <div
      style={{
        border: border.emphasis,
        background: lockedGradient,
        borderRadius: radius.card,
        padding: 28,
        display: 'flex',
        alignItems: 'center',
        gap: 32,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <TierPill tier="PRO" />
          <Mono size={10.5} tracking={0.1}>
            NOT ON YOUR PLAN YET
          </Mono>
        </div>
        <div style={{ fontSize: 21, lineHeight: 1.35, color: c.ink, maxWidth: '44ch', textWrap: 'pretty' }}>{tease}</div>
        <LinkButton onClick={onOpen} size={14}>
          See what unlocks →
        </LinkButton>
      </div>

      {ghosts ? (
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            gap: 1,
            background: 'rgba(9,64,116,0.10)',
            borderRadius: radius.list,
            overflow: 'hidden',
          }}
        >
          {[
            { label: 'VIEWINGS BOOKED', value: '——' },
            { label: 'ENQUIRY → VIEWING', value: '——%' },
            { label: 'VENDOR LEADS', value: '——' },
          ].map(g => (
            <div
              key={g.label}
              style={{
                background: 'rgba(255,255,255,0.55)',
                padding: '20px 26px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                minWidth: 150,
              }}
            >
              <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: '0.12em', color: c.ink3 }}>{g.label}</span>
              <span style={{ fontSize: 26, lineHeight: 1, color: 'rgba(11,18,32,0.22)' }}>{g.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Pro CRM-context panel on call detail. */
export function ProContextPanel({ onOpen }: { onOpen: () => void }) {
  return (
    <div
      style={{
        border: border.emphasis,
        background: lockedGradient,
        borderRadius: radius.card,
        padding: '22px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <TierPill tier="PRO" />
        <Mono size={10.5} tracking={0.1}>
          CRM CONTEXT
        </Mono>
      </div>
      <div style={{ fontSize: 15, lineHeight: 1.55, color: c.ink }}>
        On Pro, the property record, contact history and what the assistant wrote back sit right here.
      </div>
      <LinkButton onClick={onOpen} size={13.5}>
        See what unlocks →
      </LinkButton>
    </div>
  );
}

/**
 * Select knowledge-gaps panel. The design shows nine questions and three ghosted
 * examples; unanswered_questions does not exist, so the count is withheld and the
 * ghosts carry no invented question text.
 */
export function SelectGapsStrip({ onOpen }: { onOpen: () => void }) {
  return (
    <div
      style={{
        border: border.emphasis,
        background: lockedGradient,
        borderRadius: radius.card,
        padding: 28,
        display: 'flex',
        alignItems: 'center',
        gap: 32,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <TierPill tier="SELECT" />
          <Mono size={10.5} tracking={0.1}>
            KNOWLEDGE GAPS
          </Mono>
        </div>
        <div style={{ fontSize: 21, lineHeight: 1.35, color: c.ink, maxWidth: '52ch', textWrap: 'pretty' }}>
          Some callers ask questions your assistant cannot answer. Select shows you each one, and what to add.
        </div>
        <LinkButton onClick={onOpen} size={14}>
          See what unlocks →
        </LinkButton>
      </div>

      <div style={{ flex: '0 0 320px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[0, 1, 2].map(i => (
          <div
            key={i}
            style={{
              background: 'rgba(255,255,255,0.6)',
              borderRadius: 8,
              padding: '12px 14px',
              fontSize: 14,
              color: 'rgba(11,18,32,0.28)',
            }}
          >
            ————————————————————
          </div>
        ))}
      </div>
    </div>
  );
}
